import { ReceiptText } from "lucide-react";

import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import type { CardWalletMovementsListProps } from "./cardWalletMovementsList.types";
import { MovementAmount, MovementAuthorCell, MovementKindBadges } from "./MovementCells";

/**
 * The phone face, below `xl`.
 *
 * The title line is the KIND of movement, not the amount: on a phone the first question is "what is this
 * row", and the amount answers the second. The reference wraps rather than truncating — it is up to 120
 * characters of operator-written identifier and being readable is its entire purpose.
 */
export function CardWalletMovementsCards({
  movements,
  isLoading,
  currentUserId,
  highlightedProviderTxId,
}: CardWalletMovementsListProps) {
  const { t, activeLanguage } = useTranslations("cardWallets");
  const locale = activeLanguage || "es";

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3 p-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <ColocaSkeleton key={index} className="h-36 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  if (movements.length === 0) {
    return (
      <div className="p-4">
        <ColocaEmpty
          surface="card"
          icon={ReceiptText}
          title={t("empty_title")}
          description={t("empty_description")}
        />
      </div>
    );
  }

  return (
    <ul className="flex list-none flex-col gap-3 p-4">
      {movements.map((movement) => {
        const reference = movement.metadata?.external_reference;
        const referenceText = typeof reference === "string" ? reference : movement.provider_transaction_id;
        const highlighted =
          highlightedProviderTxId !== null && movement.provider_transaction_id === highlightedProviderTxId;

        return (
          <li
            key={movement.id}
            className={cn(
              "flex flex-col gap-2.5 rounded-xl border border-border bg-card p-4",
              highlighted && "border-primary/40 bg-primary/5",
            )}
          >
            <div className="flex flex-col gap-1">
              <MovementKindBadges movement={movement} />
              <span className="text-xs text-muted-foreground">
                {formatDateTime(movement.created_at, { locale })}
              </span>
            </div>

            <div className="h-px bg-border" />

            <MovementAmount movement={movement} size="md" />

            <span className="break-all font-mono text-xs text-foreground">{referenceText}</span>
            <span className="text-xs text-muted-foreground">
              <MovementAuthorCell movement={movement} currentUserId={currentUserId} variant="card" />
            </span>
          </li>
        );
      })}
    </ul>
  );
}
